import { useEffect, useState } from "react";
import { List, Menu, Compass } from "lucide-react";

interface TOCItem {
  id: string;
  label: string;
}

interface TableOfContentsProps {
  items: TOCItem[];
  title?: string;
}

export default function TableOfContents({ items, title = "Table of Contents" }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -60% 0px" } // trigger active state when heading is near top
    );

    items.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => {
      items.forEach((item) => {
        const el = document.getElementById(item.id);
        if (el) observer.unobserve(el);
      });
    };
  }, [items]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Update historical URL hash cleanly without forcing browser reload
      window.history.pushState(null, "", `#${id}`);
    }
  };

  return (
    <div className="bg-white border border-gray-150 rounded-2xl p-5 shadow-3xs w-full">
      <div className="flex items-center gap-2 pb-4 mb-4 border-b border-gray-100">
        <Compass className="w-4 h-4 text-green-600 shrink-0" />
        <span className="text-xs font-black uppercase tracking-wider text-gray-900 leading-none">
          {title}
        </span>
      </div>
      
      <nav aria-label="Table of contents" className="relative">
        {/* Left vertical timeline indicator bar */}
        <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-gray-100" />
        
        <ul className="space-y-3.5 relative">
          {items.map((item) => {
            const isActive = activeId === item.id;
            return (
              <li key={item.id} className="pl-5 relative group">
                {/* Active circle indicator dot */}
                <div 
                  className={`absolute left-[5px] top-[7px] w-[6px] h-[6px] rounded-full -translate-x-1/2 transition-all duration-300 ${
                    isActive 
                      ? "bg-green-600 ring-4 ring-green-100 scale-125" 
                      : "bg-gray-300 group-hover:bg-gray-400 group-hover:scale-110"
                  }`} 
                />
                
                <a
                  href={`#${item.id}`}
                  onClick={(e) => handleClick(e, item.id)}
                  className={`text-xs font-bold leading-relaxed tracking-tight block transition-colors duration-200 cursor-pointer ${
                    isActive 
                      ? "text-green-800 font-extrabold" 
                      : "text-gray-500 hover:text-green-700"
                  }`}
                >
                  {item.label}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
